import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import type { VantaStatus } from "./types";

export function useVantaStatus(tenantId: string) {
  return useQuery<VantaStatus>({
    queryKey: ["vanta", "status", tenantId],
    queryFn: async () => {
      const { data } = await client.get(`/tenants/${tenantId}/vanta/status/`);
      return data.data;
    },
    enabled: !!tenantId,
  });
}

export function useVantaConnect(tenantId: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await client.get(`/tenants/${tenantId}/vanta/connect/`);
      return data.data as { auth_url: string };
    },
    onSuccess: ({ auth_url }) => {
      window.location.href = auth_url;
    },
  });
}

export function useVantaDisconnect(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await client.delete(`/tenants/${tenantId}/vanta/disconnect/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vanta", "status", tenantId] });
    },
  });
}

export function useVantaPushNow(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<{ success: boolean; errors: string[]; counts: Record<string, number> }, Error>({
    mutationFn: async () => {
      const { data } = await client.post(`/tenants/${tenantId}/vanta/push/`);
      return data.data;
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["vanta", "status", tenantId] });
    },
  });
}

export function useVantaToggleAutoPush(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<{ auto_push_enabled: boolean }, Error, boolean>({
    mutationFn: async (enabled: boolean) => {
      const { data } = await client.patch(`/tenants/${tenantId}/vanta/auto-push/`, { enabled });
      return data.data;
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["vanta", "status", tenantId] });
    },
  });
}
